// File: frontend/src/pages/Research.tsx
import React, { useState } from 'react';
import SEOHead from '../components/SEOHead';
import {
    Box,
    Container,
    Paper,
    Typography,
    styled,
    alpha,
    Fade,
    Stack,
    Chip,
    Link,
    Divider,
    Button,
} from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SchoolIcon from '@mui/icons-material/School';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import BiotechIcon from '@mui/icons-material/Biotech';
import EmailIcon from '@mui/icons-material/Email';
import XIcon from '@mui/icons-material/X';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { publications, formatIeeeCitation, formatBibtex, doiUrl } from '../data/publications';
import { cyber } from '../theme';

const HeroSection = styled(Box)(({ theme }) => ({
    background: `linear-gradient(135deg, ${cyber.bgPaper} 0%, #3a2d6e 55%, ${cyber.primary} 130%)`,
    border: `1px solid ${alpha(cyber.primary, 0.35)}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(4),
    marginBottom: theme.spacing(4),
    color: 'white',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        right: '-10%',
        width: '300px',
        height: '300px',
        background: alpha(cyber.primaryLight, 0.25),
        borderRadius: '50%',
        filter: 'blur(80px)',
    }
}));

const SectionCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    marginBottom: theme.spacing(3),
    background: alpha(theme.palette.background.paper, 0.8),
    backdropFilter: 'blur(10px)',
    borderRadius: theme.spacing(2),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}));

const IconWrapper = styled(Box)(({ theme }) => ({
    width: 56,
    height: 56,
    borderRadius: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)}, ${alpha(theme.palette.secondary.main, 0.2)})`,
}));

const ResearchAreaChip = styled(Chip)(({ theme }) => ({
    margin: theme.spacing(0.5),
    padding: theme.spacing(1),
    height: 'auto',
    '& .MuiChip-label': {
        whiteSpace: 'normal',
        padding: theme.spacing(1),
    }
}));

const LimitationItem = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    background: alpha(theme.palette.warning.main, 0.05),
    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
    borderRadius: theme.spacing(1),
}));

const Research: React.FC = () => {
    const [copiedId, setCopiedId] = useState<string | null>(null);

    const copyBibtex = async (id: string, bibtex: string) => {
        try {
            await navigator.clipboard.writeText(bibtex);
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 2000);
        } catch {
            // Clipboard unavailable (e.g. insecure context): ignore silently
        }
    };

    const researchAreas = [
        'Automated IOC extraction accuracy and validation',
        'STIX 2.1 bundle generation from unstructured text',
        'TTP mapping to MITRE ATT&CK framework',
        'LLM limitations and failure modes in security contexts',
        'Bias detection in AI-generated threat assessments',
        'Human-AI collaboration in threat analysis workflows',
    ];

    const limitations = [
        {
            title: 'AI-Generated Content',
            description: 'All analytical outputs are generated by Large Language Models and may contain errors, omissions, or hallucinations. Content should never be used as the sole basis for security decisions.'
        },
        {
            title: 'IOC Verification Required',
            description: 'Extracted Indicators of Compromise (IOCs) require human verification before operational use. False positives and extraction errors are possible.'
        },
        {
            title: 'Experimental Platform',
            description: 'This platform is experimental and under active development. Features may change, be removed, or become temporarily unavailable without prior notice.'
        },
        {
            title: 'No Guaranteed Availability',
            description: 'As a research project, there is no service level agreement. The platform may be discontinued or significantly modified at any time.'
        },
    ];

    return (
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', py: 4 }}>
            <SEOHead
                title="Research Project - TI Mindmap HUB"
                description="TI Mindmap HUB is an independent research project exploring Generative AI applications in Cyber Threat Intelligence. Peer-reviewed publications, research objectives, methodology, and limitations."
                canonical="/research"
            />

            <Container maxWidth="lg">
                {/* Hero Section */}
                <Fade in timeout={500}>
                    <HeroSection>
                        <Box sx={{ position: 'relative', zIndex: 1 }}>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                                <ScienceIcon sx={{ fontSize: 48 }} />
                                <Box>
                                    <Typography variant="h3" component="h1" fontWeight="bold">
                                        Research Project
                                    </Typography>
                                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                        Independent exploration of GenAI in Cyber Threat Intelligence
                                    </Typography>
                                </Box>
                            </Stack>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                                <Chip
                                    label="Independent"
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }}
                                />
                                <Chip
                                    label="Non-Commercial"
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }}
                                />
                                <Chip
                                    label="Open Research"
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }}
                                />
                                <Chip
                                    icon={<MenuBookIcon sx={{ color: 'white !important' }} />}
                                    label="Peer-Reviewed · IEEE"
                                    component="a"
                                    href="#publications"
                                    clickable
                                    sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 'bold' }}
                                />
                            </Stack>
                        </Box>
                    </HeroSection>
                </Fade>

                {/* Publications */}
                <Fade in timeout={600}>
                    <SectionCard elevation={2} id="publications" sx={{ scrollMarginTop: 80 }}>
                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
                            <IconWrapper sx={{ background: alpha(cyber.accent.cyan, 0.14) }}>
                                <MenuBookIcon sx={{ fontSize: 28, color: cyber.accent.cyan }} />
                            </IconWrapper>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    Peer-Reviewed Publications
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    Peer-reviewed research on the automated STIX 2.1 generation approach explored in this project, co-authored by the project maintainer.
                                </Typography>
                            </Box>
                        </Stack>

                        {publications.map((pub) => (
                            <Paper
                                key={pub.id}
                                elevation={0}
                                sx={{
                                    p: 3,
                                    mb: 2,
                                    bgcolor: alpha(cyber.primary, 0.05),
                                    border: `1px solid ${alpha(cyber.primary, 0.2)}`,
                                    borderRadius: 2,
                                }}
                            >
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                                    <Chip size="small" label={pub.venue} color="primary" variant="outlined" />
                                    <Chip size="small" label={String(pub.year)} variant="outlined" />
                                </Stack>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    <Link href={pub.url} target="_blank" rel="noopener noreferrer" color="inherit" underline="hover">
                                        {pub.title}
                                    </Link>
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    {pub.authors.map((a) => `${a.name} (${a.affiliation})`).join(' · ')}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: cyber.fontMono }}>
                                    DOI:{' '}
                                    <Link href={doiUrl(pub)} target="_blank" rel="noopener noreferrer" color="primary" underline="hover">
                                        {pub.doi}
                                    </Link>
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.8, mt: 1.5 }}>
                                    {pub.summary}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                                    {pub.highlights.map((h) => (
                                        <ResearchAreaChip key={h} label={h} variant="outlined" color="secondary" size="small" />
                                    ))}
                                </Box>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2.5 }}>
                                    <Button
                                        variant="contained"
                                        href={pub.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        endIcon={<OpenInNewIcon />}
                                    >
                                        Read on IEEE Xplore
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => copyBibtex(pub.id, formatBibtex(pub))}
                                        startIcon={copiedId === pub.id ? <CheckIcon /> : <ContentCopyIcon />}
                                    >
                                        {copiedId === pub.id ? 'BibTeX copied' : 'Copy BibTeX'}
                                    </Button>
                                </Stack>
                            </Paper>
                        ))}
                    </SectionCard>
                </Fade>

                {/* Independence Statement */}
                <Fade in timeout={700}>
                    <SectionCard elevation={2}>
                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
                            <IconWrapper>
                                <VerifiedUserIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                            </IconWrapper>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    Independence Statement
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    TI Mindmap HUB is a <strong>personal, independent research project</strong>. It is not affiliated with, endorsed by, or representative of any employer, organization, or commercial entity.
                                </Typography>
                            </Box>
                        </Stack>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                bgcolor: alpha(cyber.primary, 0.05),
                                border: `1px solid ${alpha(cyber.primary, 0.2)}`,
                                borderRadius: 2,
                            }}
                        >
                            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                                All views, methodologies, analyses, and outputs presented on this platform are the author's own and <strong>do not reflect the positions, opinions, or endorsements of any third party</strong>, including current or former employers, clients, or partners.
                            </Typography>
                        </Paper>

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 3, fontStyle: 'italic' }}>
                            This project is developed entirely in personal time, using personal resources, and is driven solely by research interest in the intersection of Artificial Intelligence and Cybersecurity.
                        </Typography>
                    </SectionCard>
                </Fade>

                {/* Research Objectives */}
                <Fade in timeout={900}>
                    <SectionCard elevation={2}>
                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
                            <IconWrapper sx={{ background: alpha(cyber.accent.cyan, 0.14) }}>
                                <BiotechIcon sx={{ fontSize: 28, color: cyber.accent.cyan }} />
                            </IconWrapper>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    Research Objectives
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    This project explores the practical application of Generative AI to Cyber Threat Intelligence workflows, with a focus on understanding both capabilities and limitations.
                                </Typography>
                            </Box>
                        </Stack>

                        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
                            Areas of Investigation
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                            {researchAreas.map((area, index) => (
                                <ResearchAreaChip
                                    key={index}
                                    label={area}
                                    variant="outlined"
                                    color="primary"
                                />
                            ))}
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                            The goal is not to build a commercial product, but to <strong>contribute knowledge</strong> to the cybersecurity and AI research communities about what works, what doesn't, and where human oversight remains essential.
                        </Typography>
                    </SectionCard>
                </Fade>

                {/* Academic Collaboration */}
                <Fade in timeout={1100}>
                    <SectionCard elevation={2}>
                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
                            <IconWrapper sx={{ background: alpha(cyber.accent.green, 0.14) }}>
                                <SchoolIcon sx={{ fontSize: 28, color: cyber.accent.green }} />
                            </IconWrapper>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    Academic Collaboration
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    TI Mindmap HUB actively collaborates with universities and independent researchers to advance the understanding of AI applications in cybersecurity.
                                </Typography>
                            </Box>
                        </Stack>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                bgcolor: alpha(cyber.primary, 0.05),
                                border: `1px solid ${alpha(cyber.primary, 0.2)}`,
                                borderRadius: 2,
                                mb: 2
                            }}
                        >
                            <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                                <strong>Current Focus:</strong> STIX 2.1 bundle generation and validation, developed in collaboration with researchers from the University of Salerno and the University of Texas at San Antonio. The approach and its evaluation are described in a <Link href="#publications" color="primary" underline="hover">peer-reviewed paper</Link> published in the {publications[0].venue}.
                            </Typography>
                        </Paper>

                        <Typography variant="body2" color="text.secondary">
                            If you are a researcher or academic institution interested in collaboration, dataset access, or joint research initiatives, please reach out via the contact information below.
                        </Typography>
                    </SectionCard>
                </Fade>

                {/* Limitations */}
                <Fade in timeout={1300}>
                    <SectionCard elevation={2}>
                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
                            <IconWrapper sx={{ background: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.2)}, ${alpha(theme.palette.warning.dark, 0.2)})` }}>
                                <WarningAmberIcon sx={{ fontSize: 28, color: 'warning.main' }} />
                            </IconWrapper>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    Known Limitations
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    Transparency about limitations is essential for responsible AI research. Users should be aware of the following:
                                </Typography>
                            </Box>
                        </Stack>

                        <Box sx={{ mt: 2 }}>
                            {limitations.map((limitation, index) => (
                                <LimitationItem key={index}>
                                    <WarningAmberIcon sx={{ color: 'warning.main', flexShrink: 0, mt: 0.5 }} />
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                            {limitation.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {limitation.description}
                                        </Typography>
                                    </Box>
                                </LimitationItem>
                            ))}
                        </Box>
                    </SectionCard>
                </Fade>

                {/* Maintainer */}
                <Fade in timeout={1500}>
                    <SectionCard elevation={2}>
                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
                            <IconWrapper>
                                <PersonIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                            </IconWrapper>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    Project Maintainer
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    TI Mindmap HUB is maintained by an independent cybersecurity practitioner and researcher with experience in threat intelligence, security operations, and AI applications in security contexts.
                                </Typography>
                            </Box>
                        </Stack>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 3 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    bgcolor: alpha(cyber.primary, 0.05),
                                    border: `1px solid ${alpha(cyber.primary, 0.2)}`,
                                    borderRadius: 1,
                                    flex: 1,
                                }}
                            >
                                <EmailIcon sx={{ color: 'primary.main' }} />
                                <Link href="mailto:info@ti-mindmap-hub.com" color="primary" underline="hover">
                                    info@ti-mindmap-hub.com
                                </Link>
                            </Paper>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    bgcolor: alpha(cyber.primary, 0.05),
                                    border: `1px solid ${alpha(cyber.primary, 0.2)}`,
                                    borderRadius: 1,
                                    flex: 1,
                                }}
                            >
                                <XIcon sx={{ color: 'primary.main' }} />
                                <Link href="https://x.com/ti_mindmap_hub" target="_blank" rel="noopener noreferrer" color="primary" underline="hover">
                                    @ti_mindmap_hub
                                </Link>
                            </Paper>
                        </Stack>
                    </SectionCard>
                </Fade>

                {/* How to Cite */}
                <Fade in timeout={1700}>
                    <SectionCard elevation={2}>
                        <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
                            <IconWrapper sx={{ background: alpha(cyber.accent.coral, 0.14) }}>
                                <FormatQuoteIcon sx={{ fontSize: 28, color: cyber.accent.coral }} />
                            </IconWrapper>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    How to Cite
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    If you reference this project in academic work, publications, or presentations:
                                </Typography>
                            </Box>
                        </Stack>

                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                bgcolor: alpha(cyber.bgDefault, 0.6),
                                border: `1px solid ${alpha(cyber.primary, 0.2)}`,
                                borderRadius: 2,
                                fontFamily: cyber.fontMono,
                            }}
                        >
                            <Typography variant="body2" sx={{ fontFamily: cyber.fontMono, lineHeight: 1.8 }}>
                                TI Mindmap HUB. (2026). <em>An Experimental Platform for GenAI-Powered Cyber Threat Intelligence</em>. Available at: https://ti-mindmap-hub.com
                            </Typography>
                        </Paper>

                        {publications.map((pub) => (
                            <Box key={pub.id} sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    For the STIX 2.1 generation methodology, please cite:
                                </Typography>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        bgcolor: alpha(cyber.bgDefault, 0.6),
                                        border: `1px solid ${alpha(cyber.primary, 0.2)}`,
                                        borderRadius: 2,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontFamily: cyber.fontMono, lineHeight: 1.8, wordBreak: 'break-word' }}>
                                        {formatIeeeCitation(pub)}
                                    </Typography>
                                </Paper>
                            </Box>
                        ))}

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            For specific research outputs, datasets, or methodologies, please contact the maintainer for appropriate citation formats.
                        </Typography>
                    </SectionCard>
                </Fade>
            </Container>
        </Box>
    );
};

export default Research;